import React, { useEffect, useState } from 'react';
import { Card, CardHeader } from './ui/Card';
import { Badge } from './ui/Badge';
import { Table } from './ui/Table';
import { IS_ADMIN_SURFACE } from '../utils/surface';

const fmt = (n: any) => {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString();
};
const fmtUsd = (n: any) => {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return '$0.000000';
  if (x === 0) return '$0.000000';
  const a = Math.abs(x);
  if (a < 0.0001) return `$${x.toFixed(8)}`;
  if (a < 0.01)   return `$${x.toFixed(6)}`;
  if (a < 1)      return `$${x.toFixed(4)}`;
  return `$${x.toFixed(2)}`;
};
const fmtUnit = (n: any, u: string | null | undefined) => {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  const unit = (u || '').toLowerCase();
  if (unit === 'gb' || unit === 'gigabyte') {
    if (x >= 1024) return `${(x / 1024).toFixed(2)} TB`;
    return `${x.toLocaleString(undefined, { maximumFractionDigits: 4 })} GB`;
  }
  if (unit === 'minute' || unit === 'min') {
    if (x >= 60) return `${(x / 60).toFixed(1)} hrs`;
    return `${x.toLocaleString(undefined, { maximumFractionDigits: 2 })} min`;
  }
  if (unit === 'second') {
    if (x >= 3600) return `${(x / 3600).toFixed(2)} hrs`;
    if (x >= 60)   return `${(x / 60).toFixed(1)} min`;
    return `${x.toLocaleString()} sec`;
  }
  if (unit === 'megabyte' || unit === 'mb') {
    if (x >= 1024) return `${(x / 1024).toFixed(2)} GB`;
    return `${x.toLocaleString()} MB`;
  }
  const compact = (v: number) => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e4) return `${(v / 1e3).toFixed(1)}K`;
    return v.toLocaleString();
  };
  return `${compact(x)}${u ? ' ' + u : ''}`;
};

type EntityType = 'dealer' | 'customer' | 'property' | 'person' | 'unit';

interface Props {
  entityType: EntityType;
  entityId: string | number | null | undefined;
}

// Drives the Operations quick-view card on every detail page on the admin
// surface (Dealer / Customer / Property / Person). Hidden on app/visitor/
// tenants because dealers and customers shouldn't see infra cost data.
export const EntityUsageCard: React.FC<Props> = ({ entityType, entityId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hook must run unconditionally; effect bails internally on non-admin.
  useEffect(() => {
    if (!IS_ADMIN_SURFACE || !entityId) { setLoading(false); return; }
    const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/operations/entity-usage?type=${entityType}&id=${entityId}`, { headers })
      .then(r => r.json())
      .then(d => { if (d?.error) setError(d.error); else setData(d); })
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  // Admin-only surface gate. Card renders nothing on app/visitor/tenants —
  // dealers + property managers + tenants never see the Operations quick view.
  if (!IS_ADMIN_SURFACE) return null;
  if (!entityId) return null;
  if (loading) return <Card><div className="text-sm text-gray-400 p-4">Loading usage…</div></Card>;
  if (error || !data) return <Card><div className="text-sm text-red-500 p-4">Failed to load usage: {error || 'no data'}</div></Card>;

  // Pull every counts_* field — labels are derived from the column name so
  // adding new counters in the endpoint surfaces them here automatically.
  const counterEntries = Object.entries(data)
    .filter(([k, v]) => k.startsWith('counts_') && v != null)
    .map(([k, v]) => ({
      label: k.replace('counts_', '').replace(/_/g, ' '),
      value: Number(v),
    }));

  return (
    <Card padding={false}>
      <CardHeader
        title="Operations Quick View"
        subtitle={`Live metrics for this ${entityType}. Counts come from Postgres; usage from usage_ledger.`}
      />

      {/* Entity counts strip */}
      {counterEntries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
          {counterEntries.map(c => (
            <div key={c.label} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="text-xs uppercase tracking-wide text-gray-500">{c.label}</div>
              <div className="text-xl font-bold text-gray-900 mt-1">{fmt(c.value)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Cost totals */}
      {data.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pb-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Event Types</div>
            <div className="text-xl font-bold text-emerald-900 mt-1">{fmt(data.totals.distinct_event_types)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Total Fires</div>
            <div className="text-xl font-bold text-emerald-900 mt-1">{fmt(data.totals.total_fires_all_time)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Cost 30d</div>
            <div className="text-xl font-bold text-emerald-900 mt-1 font-mono">{fmtUsd(data.totals.cost_30d)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide text-emerald-700">Cost All-Time</div>
            <div className="text-xl font-bold text-emerald-900 mt-1 font-mono">{fmtUsd(data.totals.cost_all_time)}</div>
          </div>
        </div>
      )}

      {/* Per-provider rollup */}
      {data.by_provider?.length > 0 && (
        <div className="px-4 pb-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">By Provider</div>
          <div className="flex flex-wrap gap-2">
            {data.by_provider.map((p: any) => (
              <Badge key={p.provider} color="green">
                {p.provider}: {fmt(p.fires)} fires · {fmtUsd(p.cost)}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Per-event detail */}
      {data.events?.length > 0 ? (
        <Table
          data={data.events.map((e: any) => ({ ...e, id: e.event_type }))}
          columns={[
            { key: 'event_type', header: 'Event', render: (r: any) => (
              <div>
                <span className="font-mono text-xs">{r.event_type}</span>
                <span className="block text-[10px] text-gray-500">{r.provider}{r.unit_label ? ` · ${r.unit_label}` : ''}</span>
              </div>
            )},
            { key: 'fires_30d',   header: '30d fires',  render: (r: any) => <span className="font-mono text-xs">{fmt(r.fires_30d)}</span> },
            { key: 'qty_30d',     header: '30d usage',  render: (r: any) => <span className="font-mono text-xs text-emerald-700">{fmtUnit(r.qty_30d, r.unit_label)}</span> },
            { key: 'cost_30d',    header: '30d cost',   render: (r: any) => <span className="font-mono text-xs">{fmtUsd(r.cost_30d)}</span> },
            { key: 'qty_12mo',    header: '12mo usage', render: (r: any) => <span className="font-mono text-xs text-emerald-700">{fmtUnit(r.qty_12mo, r.unit_label)}</span> },
            { key: 'qty_all',     header: 'All usage',  render: (r: any) => <span className="font-mono text-xs font-semibold text-emerald-700">{fmtUnit(r.qty_all_time, r.unit_label)}</span> },
            { key: 'cost_all',    header: 'All cost',   render: (r: any) => <span className="font-mono text-xs font-semibold">{fmtUsd(r.cost_all_time)}</span> },
          ]}
        />
      ) : (
        <div className="px-4 pb-4 text-sm text-gray-400">
          No metered events recorded for this {entityType} yet. Usage will appear here as soon as a webhook
          (call, video room close, door unlock, etc.) fires with this {entityType}_id stamped on the ledger row.
        </div>
      )}
    </Card>
  );
};
